import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { SelectOption } from '@/components/ui/Select'
import { api } from '@/lib/api'

export interface MasterData {
  dyeSources: {
    id: string
    code: string
    name: string
    scientificName: string
    swatchHex: string | null
  }[]
  waterTypes: { id: string; name: string }[]
  fibers: { id: string; name: string }[]
  yarnTypes: { id: string; name: string }[]
  mordants: { id: string; name: string }[]
  institutions: { id: string; name: string }[]
  weaveStructures: { id: string; name: string }[]
  preTreatments: { id: string; name: string }[]
  dryingMethods: { id: string; name: string }[]
  extractionMethods: { id: string; name: string }[]
  filtrationMethods: { id: string; name: string }[]
  illuminants: { id: string; name: string }[]
  observers: { id: string; name: string }[]
  manuscripts: { id: string; name: string; year: number | null }[]
}

const EMPTY: MasterData = {
  dyeSources: [],
  waterTypes: [],
  fibers: [],
  yarnTypes: [],
  mordants: [],
  institutions: [],
  weaveStructures: [],
  preTreatments: [],
  dryingMethods: [],
  extractionMethods: [],
  filtrationMethods: [],
  illuminants: [],
  observers: [],
  manuscripts: [],
}

/**
 * Controlled vocabularies change only through Admin, so they are cached for the
 * session and shared by every form and filter panel.
 */
export function useMasterData() {
  const query = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
    staleTime: 10 * 60_000,
  })

  const data = query.data ?? EMPTY

  const options = useMemo(
    () => ({
      dyeSources: data.dyeSources.map(
        (source): SelectOption => ({
          value: source.id,
          label: source.name,
          swatch: source.swatchHex,
          description: source.scientificName,
        }),
      ),
      manuscripts: data.manuscripts.map(
        (manuscript): SelectOption => ({
          value: manuscript.id,
          label: manuscript.name,
          description: manuscript.year ? String(manuscript.year) : undefined,
        }),
      ),
      waterTypes: toSelect(data.waterTypes),
      fibers: toSelect(data.fibers),
      yarnTypes: toSelect(data.yarnTypes),
      mordants: toSelect(data.mordants),
      institutions: toSelect(data.institutions),
      weaveStructures: byName(data.weaveStructures),
      preTreatments: byName(data.preTreatments),
      dryingMethods: byName(data.dryingMethods),
      extractionMethods: byName(data.extractionMethods),
      filtrationMethods: byName(data.filtrationMethods),
      illuminants: byName(data.illuminants),
      observers: byName(data.observers),
    }),
    [data],
  )

  return { ...query, data, options }
}

/** For lists whose records store the term's name rather than its id. */
const byName = (terms: { name: string }[]): SelectOption[] =>
  terms.map((term) => ({ value: term.name, label: term.name }))

const toSelect = (terms: { id: string; name: string }[]): SelectOption[] =>
  terms.map((term) => ({ value: term.id, label: term.name }))
